import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { DataTable, type DataTableColumn } from '@/components/common';
import {
  useSupplierIntegration,
  useSuppliers,
  type SupplierFilters,
  type SupplierListItem,
} from '@/hooks/useInventory';
import { useAuthStore } from '@/stores/authStore';
import { SupplierIntegrationDialog } from './SupplierIntegrationDialog';

function contactLine(supplier: SupplierListItem): string {
  return [supplier.contactName, supplier.email, supplier.phone]
    .filter((part): part is string => typeof part === 'string' && part !== '')
    .join(' · ');
}

function SupplierStatusCell({ supplierId }: { supplierId: string }) {
  const { t } = useTranslation('inventory');
  const query = useSupplierIntegration(supplierId);

  if (query.isPending) {
    return <Chip size="small" label={t('suppliers.statusLoading')} />;
  }
  if (query.isError || !query.data) {
    return <Chip size="small" color="warning" label={t('suppliers.statusUnavailable')} />;
  }
  if (!query.data.configured) {
    return <Chip size="small" label={t('suppliers.statusUnconfigured')} />;
  }
  return (
    <Box>
      <Chip size="small" color="success" label={t('suppliers.statusConfigured')} />
      {query.data.provider !== '' && (
        <Typography variant="caption" color="text.secondary" component="div">
          {query.data.provider}
        </Typography>
      )}
    </Box>
  );
}

export default function SuppliersTab() {
  const { t } = useTranslation('inventory');
  const canEdit = useAuthStore((s) => s.hasPermission('settings.edit_workshop'));
  const [search, setSearch] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selected, setSelected] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    if (search === debouncedQuery) return;
    const handle = setTimeout(() => setDebouncedQuery(search), 300);
    return () => clearTimeout(handle);
  }, [search, debouncedQuery]);

  const filters = useMemo(() => {
    const next: SupplierFilters = {};
    if (debouncedQuery) next.q = debouncedQuery;
    return next;
  }, [debouncedQuery]);

  const suppliersQuery = useSuppliers(filters);
  const suppliers = useMemo(
    () => suppliersQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [suppliersQuery.data],
  );

  const openSupplier = (supplier: SupplierListItem) => {
    setSelected({ id: supplier.id, name: supplier.name });
  };

  const actionLabel = (supplier: SupplierListItem) =>
    canEdit
      ? t('suppliers.integration.configureNamed', { name: supplier.name })
      : t('suppliers.integration.viewNamed', { name: supplier.name });

  const columns: DataTableColumn<SupplierListItem>[] = [
    {
      header: t('suppliers.colName'),
      renderCell: (supplier) => (
        <Typography variant="body2" fontWeight={600}>
          {supplier.name}
        </Typography>
      ),
    },
    {
      header: t('suppliers.colCode'),
      renderCell: (supplier) => <Typography variant="body2">{supplier.code}</Typography>,
    },
    {
      header: t('suppliers.colContact'),
      renderCell: (supplier) => (
        <Typography variant="body2" color="text.secondary">
          {contactLine(supplier)}
        </Typography>
      ),
    },
    {
      header: t('suppliers.colStatus'),
      renderCell: (supplier) => <SupplierStatusCell supplierId={supplier.id} />,
    },
    {
      header: t('actions'),
      align: 'right',
      renderCell: (supplier) => (
        <Button size="small" variant="outlined" aria-label={actionLabel(supplier)} onClick={() => openSupplier(supplier)}>
          {canEdit ? t('suppliers.integration.configure') : t('suppliers.integration.view')}
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          {t('suppliers.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('suppliers.subtitle')}
        </Typography>
      </Box>

      <TextField
        size="small"
        placeholder={t('suppliers.searchPlaceholder')}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        sx={{ width: { xs: '100%', md: 500 }, mb: 2 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <Tooltip title={t('clearSearch')}>
                  <IconButton size="small" onClick={() => setSearch('')} aria-label={t('clearSearch')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : null,
          },
        }}
      />

      <DataTable
        columns={columns}
        data={suppliers}
        getRowKey={(supplier) => supplier.id}
        loading={suppliersQuery.isLoading}
        ariaLabel={t('suppliers.title')}
        emptyMessage={suppliersQuery.isError ? t('suppliers.loadFailed') : t('suppliers.empty')}
        {...(suppliersQuery.isError
          ? {
              emptyAction: {
                label: t('suppliers.retry'),
                onClick: () => {
                  void suppliersQuery.refetch();
                },
              },
            }
          : { emptyDescription: t('suppliers.emptyDescription') })}
        renderMobileCard={(supplier) => (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {supplier.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {supplier.code}
                </Typography>
              </Box>
              <SupplierStatusCell supplierId={supplier.id} />
            </Box>
            {contactLine(supplier) !== '' && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {contactLine(supplier)}
              </Typography>
            )}
            <Button size="small" variant="outlined" aria-label={actionLabel(supplier)} onClick={() => openSupplier(supplier)}>
              {canEdit ? t('suppliers.integration.configure') : t('suppliers.integration.view')}
            </Button>
          </Box>
        )}
      />

      {suppliersQuery.hasNextPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            onClick={() => {
              void suppliersQuery.fetchNextPage();
            }}
            disabled={suppliersQuery.isFetchingNextPage}
          >
            {t('suppliers.loadMore')}
          </Button>
        </Box>
      )}

      <SupplierIntegrationDialog
        open={selected !== null}
        supplier={selected}
        onClose={() => setSelected(null)}
      />
    </Box>
  );
}
